/**
 * QUI EST PRÉVENU, L'ÉTAT DE SON NUMÉRO, ET CE QUE SONT DEVENUS SES TESTS (LOT F4).
 * ============================================================================
 *
 * Fonctions pures. Trois règles :
 *  1. un destinataire absent de la liste des membres reste SANS NOM, dit comme
 *     tel, jamais par son identifiant ; une liste des membres non lue n'est pas
 *     une absence : le nom vient alors des personnes servies avec la
 *     surveillance, et l'état du numéro reste inconnu ;
 *  2. l'état du numéro se lit sur `phone_present` et `phone_a_corriger` ; un
 *     état non servi se dit « numéro non disponible », jamais « sans numéro » ;
 *     le masque ne se montre que si l'écran a le droit de le montrer (verdict
 *     `voir_numero_membre` de la matrice, passé par l'appelant) ;
 *  3. un message de test se dit en toutes lettres ; un motif d'échec inconnu
 *     prend la phrase neutre, jamais un vide.
 */
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import { MESSAGES_ALARMES_V2 } from "@modules/alarmes-v2/i18n/fr"
import type {
    AlarmesV2Destinataire,
    AlarmesV2LigneTest,
    AlarmesV2Membre,
    AlarmesV2Personne,
} from "@modules/alarmes-v2/types/alarmes-v2"
import {
    nomDeLaPersonne,
    raisonDeNonEnvoi,
} from "@modules/alarmes-v2/utils/journal"
import { formaterInstant } from "@modules/alarmes-v2/utils/mesure"
import type { StatutMembres } from "@modules/alarmes-v2/stores/membres.store"

/** Une clé propre de l'objet, jamais une clé héritée (`constructor`…). */
const possede = (o: object, cle: string): boolean =>
    Object.prototype.hasOwnProperty.call(o, cle)

/** L'état du numéro d'un membre. */
export type EtatNumero = "renseigne" | "sans_numero" | "a_corriger" | "inconnu"

/** Une ligne du tableau « Qui est prévenu ». */
export interface LignePrevenu {
    idUser: string
    nom: string
    /** Le rôle en toutes lettres, `""` s'il n'est pas su. */
    role: string
    /** Vrai quand la personne ne figure pas parmi les membres lus. */
    horsDesMembres: boolean
    etatNumero: EtatNumero
    /** Ce que l'écran dit du numéro : l'état, ou le masque si permis. */
    numero: string
    sms: boolean
    mail: boolean
}

/**
 * L'état du numéro, sur les deux booléens servis.
 * @param membre - le membre lu, ou `null`
 */
export function etatDuNumero(membre: AlarmesV2Membre | null): EtatNumero {
    if (!membre) return "inconnu"
    if (membre.phone_present === true) return "renseigne"
    if (membre.phone_a_corriger === true) return "a_corriger"
    if (membre.phone_present === false && membre.phone_a_corriger === false)
        return "sans_numero"
    return "inconnu"
}

/**
 * Ce que l'écran dit du numéro.
 * @param membre - le membre lu, ou `null`
 * @param voitLeNumero - le verdict `voir_numero_membre` est actif
 * @param t - la traduction
 */
export function phraseDuNumero(
    membre: AlarmesV2Membre | null,
    voitLeNumero: boolean,
    t: Traduire
): string {
    const etat = etatDuNumero(membre)
    if (etat === "renseigne") {
        const masque = membre?.phone_masque
        return voitLeNumero && typeof masque === "string" && masque !== ""
            ? masque
            : t("alarmesV2.numero.renseigne")
    }
    if (etat === "a_corriger") return t("alarmesV2.numero.aCorriger")
    if (etat === "sans_numero") return t("alarmesV2.numero.sansNumero")
    return t("alarmesV2.numero.inconnu")
}

/** Le rôle en toutes lettres, ou `""`. */
function nomDuRole(role: unknown, t: Traduire): string {
    const roles = MESSAGES_ALARMES_V2.prevenus.roles as Record<string, string>
    return typeof role === "string" && possede(roles, role)
        ? t(`alarmesV2.prevenus.roles.${role}`)
        : ""
}

/**
 * Les lignes du tableau, une par destinataire servi.
 * @param destinataires - `destinataires` de la surveillance
 * @param membres - les membres lus, par identifiant
 * @param statutMembres - où en est la lecture des membres
 * @param personnes - les noms servis avec la surveillance
 * @param voitLeNumero - le verdict `voir_numero_membre` est actif
 * @param t - la traduction
 */
export function lignesPrevenus(
    destinataires: AlarmesV2Destinataire[],
    membres: Record<string, AlarmesV2Membre>,
    statutMembres: StatutMembres,
    personnes: Record<string, AlarmesV2Personne>,
    voitLeNumero: boolean,
    t: Traduire
): LignePrevenu[] {
    const membresLus = statutMembres === "pret"
    return destinataires
        .filter(
            (d): d is AlarmesV2Destinataire & { _id_user: string } =>
                typeof d?._id_user === "string" && d._id_user !== ""
        )
        .map((d) => {
            const membre = membresLus ? (membres[d._id_user] ?? null) : null
            const horsDesMembres = membresLus && membre === null
            const canaux = Array.isArray(d.canaux) ? d.canaux : []
            let nom: string
            if (membre) {
                nom =
                    [membre.prenom, membre.nom]
                        .filter((x): x is string => !!x)
                        .join(" ") || nomDeLaPersonne(d._id_user, personnes, t)
            } else if (horsDesMembres) {
                nom = t("alarmesV2.prevenus.horsDesMembres")
            } else {
                nom = nomDeLaPersonne(d._id_user, personnes, t)
            }
            return {
                idUser: d._id_user,
                nom,
                role: membre ? nomDuRole(membre.role, t) : "",
                horsDesMembres,
                etatNumero: etatDuNumero(membre),
                numero: phraseDuNumero(membre, voitLeNumero, t),
                sms: canaux.includes("sms"),
                mail: canaux.includes("mail"),
            }
        })
}

/** Le motif d'échec en toutes lettres (`motif_echec` servi). */
export function motifDEchec(
    motif: string | null | undefined,
    canal: string | null | undefined,
    t: Traduire
): string {
    const motifs = MESSAGES_ALARMES_V2.tests.motifs as Record<string, string>
    if (!motif) return t("alarmesV2.tests.motifs.inconnu")
    const parCanal = `${motif}_${canal === "sms" ? "sms" : "mail"}`
    if (possede(motifs, parCanal))
        return t(`alarmesV2.tests.motifs.${parCanal}`)
    if (possede(motifs, motif) && motif !== "inconnu")
        return t(`alarmesV2.tests.motifs.${motif}`)
    return t("alarmesV2.tests.motifs.inconnu")
}

/** Le résultat d'un message de test, en toutes lettres, et son ton. */
export function resultatDuTest(
    ligne: AlarmesV2LigneTest,
    t: Traduire
): { texte: string; ton: "ok" | "neutre" | "warn" | "danger" } {
    const quand = (iso: string | null | undefined) => formaterInstant(iso, t)
    switch (ligne.statut) {
        case "remis": {
            const q = quand(ligne.remis_le)
            return {
                texte: q
                    ? t("alarmesV2.tests.resultats.remis", { quand: q })
                    : t("alarmesV2.tests.resultats.remisSansHeure"),
                ton: "ok",
            }
        }
        case "ouvert": {
            const q = quand(ligne.ouvert_le)
            return {
                texte: q
                    ? t("alarmesV2.tests.resultats.ouvert", { quand: q })
                    : t("alarmesV2.tests.resultats.remisSansHeure"),
                ton: "ok",
            }
        }
        case "accepte":
            return ligne.remise_non_confirmee_le
                ? {
                      texte: t("alarmesV2.tests.resultats.remiseNonConfirmee"),
                      ton: "warn",
                  }
                : {
                      texte: t("alarmesV2.tests.resultats.attente"),
                      ton: "neutre",
                  }
        case "a_envoyer":
        case "demande":
            return {
                texte: t("alarmesV2.tests.resultats.enCours"),
                ton: "neutre",
            }
        case "echoue":
            return {
                texte: t("alarmesV2.tests.resultats.nonRemis", {
                    motif: motifDEchec(ligne.motif_echec, ligne.canal, t),
                }),
                ton: "danger",
            }
        case "rejete":
            return {
                texte: t("alarmesV2.tests.resultats.nonEnvoye", {
                    raison: raisonDeNonEnvoi(
                        ligne.motif_non_envoi_canal,
                        ligne.canal,
                        t
                    ),
                }),
                ton: "warn",
            }
        default:
            return {
                texte: t("alarmesV2.tests.resultats.inconnu"),
                ton: "neutre",
            }
    }
}

/** Le nom d'un canal servi, ou « Canal non disponible ». */
function nomDuCanalDuTest(canal: unknown, t: Traduire): string {
    const canaux = MESSAGES_ALARMES_V2.envois.canaux as Record<string, string>
    return typeof canal === "string" && possede(canaux, canal)
        ? t(`alarmesV2.envois.canaux.${canal}`)
        : t("alarmesV2.envois.canalInconnu")
}

/** « SMS, demandé le 05/10 à 10h00 : Reçu le 05/10 à 10h01. » */
export function phraseDuTest(
    ligne: AlarmesV2LigneTest,
    t: Traduire
): { texte: string; ton: "ok" | "neutre" | "warn" | "danger" } {
    const r = resultatDuTest(ligne, t)
    const canal = nomDuCanalDuTest(ligne.canal, t)
    const quand = formaterInstant(ligne.demande_le, t)
    return {
        texte: quand
            ? t("alarmesV2.tests.ligne", { canal, quand, resultat: r.texte })
            : t("alarmesV2.tests.ligneSansHeure", { canal, resultat: r.texte }),
        ton: r.ton,
    }
}
